import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Vehicle, VehicleCardComponent } from '../vehicle-card/vehicle-card.component';
import { AddVehicleCardComponent } from '../add-vehicle-card/add-vehicle-card.component';
import { DeleteVehicleAlertModalComponent } from '../../shared/components/delete-vehicle-alert-modal/delete-vehicle-alert-modal.component';
import { AlertModalComponent } from '../../shared/components/alert-modal/alert-modal.component';
import {
  activeMembershipDeleteMessage,
  isActiveMembershipDeleteBlock,
} from '../../shared/utils/vehicle-delete-block.util';
import { Customer } from '../customer-table/customer-table.component';
import { VehicleService } from '../../services/vehicle.service';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Displays and manages a list of vehicles for a given customer, including loading from the API, adding, editing and deleting vehicles.
 *
 * Responsibilities:
 * - Loads vehicles for the provided customer and keeps local state (vehicles, loading, error).
 * - Opens and closes the "add/edit vehicle" card and the delete confirmation modal.
 * - Handles add/update events from child components and removes vehicles locally after successful deletion.
 * - Emits vehiclesChanged after any successful add/update/delete so the customers table behind the
 *   Customer Details modal (Vehicles and License Plate columns) can reload.
 * - Uses VehicleService to fetch and delete vehicles and NotificationService to surface success/error notifications.
 *
 * Key methods:
 * - loadVehicles(): Fetches vehicles for the current customer, transforms API response into the Vehicle interface, and updates local state.
 * - onAddVehicle(): Prepares the UI to add a new vehicle.
 * - onVehicleAdded(vehicle): Reloads the vehicle list after a vehicle is added.
 * - onVehicleUpdated(vehicle): Merges updated vehicle data into the local vehicles array and clears edit mode.
 * - onCardClosed(): Closes add/edit card and resets edit state.
 * - onEditVehicle(vehicle): Enables edit mode and populates the add/edit card with the selected vehicle.
 * - onShowDeleteModal(vehicle): Opens the delete confirmation modal for the selected vehicle.
 * - onCloseDeleteModal(): Closes the delete modal and resets delete-related state.
 * - onConfirmDelete(vehicle): Calls the API to delete a vehicle, shows messages, and removes the vehicle from the local list on success.
 *
 * Important lifecycle hooks:
 * - ngOnInit(): Invokes loadVehicles() to fetch the list when the component initializes.
 * - ngOnDestroy(): Cleans up subscriptions by completing the internal destroy$ Subject to avoid memory leaks.
 *
 * @component
 * @selector app-vehicles-list
 * @example
 *   <app-vehicles-list [customer]="selectedCustomer"></app-vehicles-list>
 * @input {Customer | null} customer - The customer whose vehicles are displayed. When present the component will load vehicles using customer.id.
 * @output {none} none - This component does not expose Angular @Output events; it handles child component events internally (e.g., vehicle added/updated) and communicates via services/state.
 */
@Component({
  selector: 'app-vehicles-list',
  standalone: true,
  imports: [
    CommonModule,
    VehicleCardComponent,
    AddVehicleCardComponent,
    DeleteVehicleAlertModalComponent,
    AlertModalComponent,
  ],
  templateUrl: './vehicles-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./vehicles-list.component.scss'],
})
export class VehiclesListComponent implements OnInit, OnDestroy {
  @Input() customer: Customer | null = null;
  /** Fired after a vehicle is added, updated or deleted on the server. */
  @Output() vehiclesChanged = new EventEmitter<void>();
  vehicles: Vehicle[] = [];
  loading = false;
  error: string | null = null;

  private destroy$ = new Subject<void>();
  showAddVehicleCard = false;
  editMode = false;
  vehicleToEdit: Vehicle | null = null;

  // Delete modal state
  showDeleteModal = false;
  vehicleToDelete: Vehicle | null = null;
  isDeleting = false;
  /**
   * Set when the server refuses the delete because a membership still covers the
   * vehicle (CWS-1384); null the rest of the time. Drives its own modal.
   */
  membershipBlockMessage: string | null = null;

  constructor(
    private vehicleService: VehicleService,
    private messageService: NotificationService
  ) {}

  ngOnInit() {
    this.loadVehicles();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load vehicles for the current customer and update component state (this.vehicles, this.loading, this.error).
   * @example
   * this.oadVehicles()
   * // returns void
   * @returns {{void}} No return value; updates component state and handles loading/error states, logging errors to the console.
   **/
  loadVehicles() {
    if (!this.customer?.id) {
      return;
    }

    this.loading = true;
    this.error = null;

    this.vehicleService
      .getVehiclesByCustomerId(this.customer.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.loading = false;
          if (response.status && response.data) {
            // Transform API response to match Vehicle interface
            this.vehicles = response.data.map(vehicle => ({
              id: vehicle.id || '',
              name:
                `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`.trim() ||
                'Unknown Vehicle',
              licensePlate: vehicle.license_plate || vehicle.licensePlate || '',
              vin: vehicle.vin || '',
              isFamilyPlanVehicle: vehicle.isFamilyPlanVehicle || false,
              state: vehicle.state || '',
            }));
          } else {
            this.vehicles = [];
          }
        },
        error: error => {
          this.loading = false;
          this.error = 'Failed to load vehicles. Please try again.';
          console.error('Error loading vehicles:', error);
        },
      });
  }

  onAddVehicle() {
    this.editMode = false;
    this.vehicleToEdit = null;
    this.showAddVehicleCard = true;
  }

  onVehicleAdded(vehicle: any) {
    // Reload vehicles from API after adding a new one
    this.loadVehicles();
    this.vehiclesChanged.emit();
  }

  onVehicleUpdated(vehicle: any) {
    // Update the vehicle in the local list
    const index = this.vehicles.findIndex(v => v.id === vehicle.id);
    if (index !== -1) {
      this.vehicles[index] = { ...this.vehicles[index], ...vehicle };
    }
    // Clear edit mode to re-enable the card
    this.editMode = false;
    this.vehicleToEdit = null;
    // Reload so the card shows what the server stored (plate is upper-cased there),
    // and tell the parent so the customers table re-reads Vehicles / License Plate.
    this.loadVehicles();
    this.vehiclesChanged.emit();
  }

  onCardClosed() {
    this.showAddVehicleCard = false;
    this.editMode = false;
    this.vehicleToEdit = null;
  }

  onEditVehicle(vehicle: Vehicle) {
    // Set edit mode and show the add vehicle card with existing data
    this.editMode = true;
    this.vehicleToEdit = vehicle;
    this.showAddVehicleCard = true;
  }

  onDeleteVehicle(vehicle: Vehicle) {
    // Handle delete vehicle action
  }

  onShowDeleteModal(vehicle: Vehicle) {
    this.vehicleToDelete = vehicle;
    this.showDeleteModal = true;
  }

  /** Dismiss the "membership still active" notice. */
  onCloseMembershipBlock() {
    this.membershipBlockMessage = null;
  }

  onCloseDeleteModal() {
    this.showDeleteModal = false;
    this.vehicleToDelete = null;
    this.isDeleting = false;
  }

  /**
   * Confirm and perform deletion of a vehicle: calls the backend to delete the vehicle, updates the local list, shows success/error messages, and closes the delete modal.
   * @example
   * nConfirmDelete({ id: 'abc123', make: 'Toyota' })
   * undefined
   * @param {{Vehicle}} {{vehicle}} - Vehicle object to delete; must include a valid id property.
   * @returns {{void}} No return value; performs side effects (service call, UI updates, notifications).
   **/
  onConfirmDelete(vehicle: Vehicle) {
    if (!vehicle.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Vehicle ID is missing.',
      });
      return;
    }

    this.isDeleting = true;

    this.vehicleService
      .deleteVehicle(vehicle.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response.status) {
            this.messageService.add({
              severity: 'success',
              summary: 'Vehicle deleted!',
              detail: `The vehicle has been removed from the customer’s profile.`,
            });

            // Remove vehicle from local list
            this.vehicles = this.vehicles.filter(v => v.id !== vehicle.id);
            this.vehiclesChanged.emit();

            // Close modal
            this.onCloseDeleteModal();
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Delete Failed',
              detail: response.message || 'Failed to delete the vehicle. Please try again.',
            });
            this.isDeleting = false;
          }
        },
        error: error => {
          // A membership on the car is not a failed request: the operator has a next
          // step, so close the confirm and put the cancel path in front of them.
          if (isActiveMembershipDeleteBlock(error)) {
            this.membershipBlockMessage = activeMembershipDeleteMessage(error);
            this.onCloseDeleteModal();
            return;
          }
          this.messageService.add({
            severity: 'error',
            summary: 'Delete Failed',
            detail:
              error.error?.message ||
              'An error occurred while deleting the vehicle. Please try again.',
          });
          this.isDeleting = false;
        },
      });
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
